import { FunctionComponent, useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/Button/Button";
import { ButtonColor } from "@/components/Button/types";
import { SelectField, SelectFieldValue } from "@/components/SelectField/SelectField";

import { ListPreview, ListPreviewStatus } from "../schema/listPreview";

interface PreviewOutcomeFooterProps {
	preview: ListPreview;
	onRerun: () => void;
	onExtend: (additionalCount: 10 | 20 | 50) => void;
	isExtendLoading: boolean;
}

const EXTEND_COUNT_OPTIONS = [
	{ title: "10 companies", value: 10 },
	{ title: "20 companies", value: 20 },
	{ title: "50 companies", value: 50 },
];

export const PreviewOutcomeFooter: FunctionComponent<PreviewOutcomeFooterProps> = ({
	preview,
	onRerun,
	onExtend,
	isExtendLoading,
}) => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`list.preview.${key}`);
	const [extendCount, setExtendCount] = useState<SelectFieldValue>(20);

	const isCompleted = preview.status === ListPreviewStatus.COMPLETED;
	const isStopped = preview.status === ListPreviewStatus.STOPPED;

	return (
		<div className="mt-4 rounded-lg border border-border-light bg-brand-white p-4">
			<div className="flex items-center gap-2 mb-3">
				<div
					className={`h-2 w-2 rounded-full ${isCompleted ? "bg-status-success" : "bg-status-warning"}`}
				/>
				<span className="text-sm font-medium text-text">
					{isCompleted ? ts("completed") : isStopped ? ts("stopped") : ts("failed")}
				</span>
			</div>

			{/* Stats summary */}
			<div className="flex items-center gap-6 mb-4">
				<div className="text-xs text-text-meta">
					{ts("found")}: <span className="font-medium text-text">{preview.foundCount}</span>
				</div>
				<div className="text-xs text-text-meta">
					{ts("unsure")}: <span className="font-medium text-text">{preview.unsureCount}</span>
				</div>
				<div className="text-xs text-text-meta">
					{ts("notFound")}: <span className="font-medium text-text">{preview.notFoundCount}</span>
				</div>
				<div className="text-xs text-text-meta">
					{ts("creditsUsed")}:{" "}
					<span className="font-medium text-text">
						{preview.foundCount + preview.unsureCount + preview.notFoundCount}
					</span>
				</div>
				{preview.estimatedFullListCredits !== null && (
					<div className="text-xs text-text-meta">
						{ts("estimatedFullListCredits")}:{" "}
						<span className="font-medium text-text">{preview.estimatedFullListCredits}</span>
					</div>
				)}
			</div>

			{/* Action buttons */}
			<div className="flex items-center gap-4">
				<Button color={ButtonColor.ACTION_SECONDARY} title={ts("rerunPreview")} onClick={onRerun} />
				<div className="h-8 border-l border-border-light" />
				<div className="flex items-center gap-2">
					<div className="w-48">
						<SelectField
							handleSelect={(value) => setExtendCount(value)}
							hideEmptyValue
							options={EXTEND_COUNT_OPTIONS}
							selectedOption={extendCount}
						/>
					</div>
					<Button
						isLoading={isExtendLoading}
						title={ts("extendPreview")}
						onClick={() => onExtend(extendCount as 10 | 20 | 50)}
					/>
				</div>
			</div>
		</div>
	);
};
